import React, { useMemo } from "react";
import { UtensilsCrossed, Coffee, IceCream, Beer, Fish, Pizza, Wine, Cookie } from "lucide-react";

/**
 * Fijnere subtype-chips per categorie. Werkt volledig client-side:
 * items worden gescoord op naam-keywords en de chip filtert lokaal.
 *
 * Als de bedrijven op termijn echte `tags` in de DB krijgen, kunnen we
 * die logica hier vervangen door tag-matching zonder de rest te raken.
 */

// Definitie: per categorie een set subtypes met:
// - key: intern (voor URL/state)
// - icon: lucide icoon
// - labels: NL/DE/EN
// - match(name, listing): boolean
const SUBTYPE_MAP = {
  "eten-drinken": [
    {
      key: "restaurant",
      icon: UtensilsCrossed,
      labels: { nl: "Restaurant", de: "Restaurant", en: "Restaurant" },
      match: (n) => /restaurant|brasserie|bistro|eeter/i.test(n),
    },
    {
      key: "cafe",
      icon: Coffee,
      labels: { nl: "Café", de: "Café", en: "Café" },
      match: (n) => /caf[eé]|koffie|coffee|tea|thee/i.test(n),
    },
    {
      key: "ijs",
      icon: IceCream,
      labels: { nl: "IJs", de: "Eis", en: "Ice cream" },
      match: (n) => /ijs|gelato|scoop/i.test(n) && !/ijssel/i.test(n),
    },
    {
      key: "bar",
      icon: Beer,
      labels: { nl: "Bar & Pub", de: "Bar & Pub", en: "Bar & Pub" },
      match: (n) => /\bbar\b|\bpub\b|bierstube|kroeg|proeflokaal/i.test(n),
    },
    {
      key: "vis",
      icon: Fish,
      labels: { nl: "Vis", de: "Fisch", en: "Fish" },
      match: (n) => /\bvis\b|fisch|seafood|kibbeling|palingrokerij/i.test(n),
    },
    {
      key: "pizza",
      icon: Pizza,
      labels: { nl: "Pizza & Pasta", de: "Pizza & Pasta", en: "Pizza & Pasta" },
      match: (n) => /pizza|italia|pasta|sushi/i.test(n),
    },
    {
      key: "snack",
      icon: Cookie,
      labels: { nl: "Snack & To-go", de: "Snack & To-go", en: "Snack & To-go" },
      match: (n) => /snack|friet|patat|burger|kebab|shoarma|broodje|bakker/i.test(n),
    },
    {
      key: "borrel",
      icon: Wine,
      labels: { nl: "Borrel & Wijn", de: "Aperitif & Wein", en: "Drinks & Wine" },
      match: (n) => /wijn|wine|slijter|borrel|vermout|likeur/i.test(n),
    },
  ],
  winkelen: [
    {
      key: "mode",
      icon: null,
      labels: { nl: "Mode & Kleding", de: "Mode & Kleidung", en: "Fashion" },
      match: (n) => /mode|kleding|fashion|boutique|jeans|schoenen|shoe/i.test(n),
    },
    {
      key: "bloem",
      icon: null,
      labels: { nl: "Bloemen & Planten", de: "Blumen & Pflanzen", en: "Flowers & Plants" },
      match: (n) => /bloem|flower|florist|tuincentr|plant/i.test(n),
    },
    {
      key: "cadeau",
      icon: null,
      labels: { nl: "Cadeau & Woon", de: "Geschenke & Wohnen", en: "Gifts & Home" },
      match: (n) => /cadeau|gift|woon|interieur|home|deco|geschenk/i.test(n),
    },
    {
      key: "boek",
      icon: null,
      labels: { nl: "Boeken & Kantoor", de: "Bücher & Büro", en: "Books & Office" },
      match: (n) => /boek|book|kantoorart|bibliotheek/i.test(n),
    },
    {
      key: "juwelier",
      icon: null,
      labels: { nl: "Juwelier", de: "Juwelier", en: "Jeweller" },
      match: (n) => /juwel|sieraad|goud|horloge|watch/i.test(n),
    },
    {
      key: "super",
      icon: null,
      labels: { nl: "Supermarkt", de: "Supermarkt", en: "Supermarket" },
      match: (n) => /albert heijn|ah\b|jumbo|aldi|lidl|coop|plus|spar|super/i.test(n),
    },
  ],
  diensten: [
    {
      key: "kapper",
      icon: null,
      labels: { nl: "Kapper & Beauty", de: "Friseur & Beauty", en: "Hair & Beauty" },
      match: (n) => /kapper|hair|barber|beauty|salon|nagel|nail/i.test(n),
    },
    {
      key: "gezondheid",
      icon: null,
      labels: { nl: "Gezondheid", de: "Gesundheit", en: "Health" },
      match: (n) => /tandarts|fysio|huisarts|apotheek|dokter|dental|physio|therap|zorg/i.test(n),
    },
    {
      key: "financieel",
      icon: null,
      labels: { nl: "Financieel & Juridisch", de: "Finanzen & Recht", en: "Finance & Legal" },
      match: (n) => /notaris|advocat|makelaar|assurantie|verzeker|financ|accountant|belasting/i.test(n),
    },
    {
      key: "auto-fiets",
      icon: null,
      labels: { nl: "Auto & Fiets", de: "Auto & Fahrrad", en: "Car & Bike" },
      match: (n) => /auto|garage|fiets|bike|scooter|banden/i.test(n),
    },
    {
      key: "bouw",
      icon: null,
      labels: { nl: "Bouw & Onderhoud", de: "Bau & Instandhaltung", en: "Building & Maintenance" },
      match: (n) => /bouw|klus|schilder|loodgiet|installat|electric|dak|timmerman|onderhoud/i.test(n),
    },
  ],
};

export const filterBySubtype = (items, category, subtype) => {
  if (!subtype) return items;
  const defs = SUBTYPE_MAP[category];
  if (!defs) return items;
  const def = defs.find((d) => d.key === subtype);
  if (!def) return items;
  return items.filter((it) => def.match(it.name || "", it));
};

export const SubtypeChips = ({ category, items, activeKey, onChange, lang = "nl" }) => {
  const defs = SUBTYPE_MAP[category];
  const chips = useMemo(() => {
    if (!defs) return [];
    return defs
      .map((d) => ({
        ...d,
        count: items.filter((it) => d.match(it.name || "", it)).length,
      }))
      .filter((d) => d.count > 0);
  }, [defs, items]);

  if (!defs || chips.length < 2) return null;

  return (
    <div data-testid="subtype-chips" className="mb-6">
      <div
        className="flex flex-wrap gap-2"
        role="group"
        aria-label="Subtype filter"
      >
        {chips.map((d) => {
          const active = activeKey === d.key;
          const Icon = d.icon;
          const label = d.labels[lang] || d.labels.nl;
          return (
            <button
              key={d.key}
              type="button"
              data-testid={`subtype-chip-${d.key}`}
              aria-pressed={active}
              onClick={() => onChange(active ? "" : d.key)}
              className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm font-[Poppins] font-semibold transition border ${
                active
                  ? "bg-[#2E9CD6] text-white border-[#2E9CD6] shadow-sm"
                  : "bg-white text-[#173A5E] border-[#E4EEF5] hover:border-[#6BC0E8]"
              }`}
            >
              {Icon && <Icon className="w-4 h-4" />}
              {label}
              <span
                className={`text-[10px] ${
                  active ? "text-white/85" : "text-[#5A7594]"
                }`}
              >
                {d.count}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default SubtypeChips;
